<template>
  <section class="rasha-profile">
    <div class="container px-4 lg:px-8">
      <article class="rasha-section">
        <p class="rasha-section__kicker">01 — Profile</p>
        <h1>A Unique Dual Perspective</h1>
        <div class="rasha-section__copy">
          <p>
            Rasha Corti is a Syria market advisor and business facilitator
            specializing in partner identification, stakeholder
            relationship-building, and coordination between international
            organizations and local Syrian partners.
          </p>
          <p>
            Born in Raqqa, Syria, and holding both Austrian and Syrian
            citizenship, she brings a unique dual perspective that combines deep
            knowledge of Syrian business environments, regulatory frameworks,
            and on-ground realities with strong relationships across Syrian
            stakeholders—government officials, business leaders, legal
            professionals, civil society figures, and regulatory experts.
          </p>
          <p>
            Her core work focuses on identifying the right partners for your
            organization, facilitating introductions, coordinating due diligence
            with local professionals, and helping you navigate relationships with
            local stakeholders. She is a trusted advisor and facilitator to
            international organizations, government representatives, and
            private-sector stakeholders seeking to enter and operate in Syria.
          </p>
          <p>
            She combines market expertise with hands-on knowledge and leverages
            an extensive network to identify suitable partners, facilitate
            introductions, coordinate assessments, and help coordinate between
            your organization and local legal and business professionals.
          </p>
        </div>
        <aside class="rasha-callout">
          <strong>Field Experience:</strong> Regular engagement in Syria; direct
          relationships with key stakeholders
        </aside>
      </article>

      <article class="rasha-section">
        <p class="rasha-section__kicker">02 — Expertise</p>
        <h1>
          Expert Advisory at the Intersection of Business Facilitation, Market
          Knowledge, and Local Relationships
        </h1>
        <p class="rasha-section__intro">
          Each engagement combines field research, direct stakeholder
          consultation, network coordination, and applied market expertise to
          enable low-risk partner identification and sustainable partnerships.
        </p>

        <div class="rasha-cards">
          <section v-for="item in expertise" :key="item.title" class="rasha-card">
            <h2>{{ item.title }}</h2>
            <p>{{ item.text }}</p>
            <p class="rasha-card__for"><strong>For:</strong> {{ item.for }}</p>
          </section>
        </div>

        <section class="rasha-card rasha-card--wide">
          <h2>Advisory &amp; Syria Market Entry Coordination</h2>
          <p>
            I support organizations seeking to enter, navigate, and scale
            operations in Syria through strategic partner identification and
            market facilitation. Whether you're exploring a new market, vetting
            a potential partner, connecting with legal and regulatory advisors,
            or scaling an existing operation, I combine on-the-ground insight,
            market knowledge, and network access to enable informed
            decision-making and sustainable success.
          </p>
          <div class="rasha-card__columns">
            <div>
              <h3>Organizations I work with:</h3>
              <ul>
                <li>Private-sector investors &amp; corporations</li>
                <li>International development agencies &amp; organizations</li>
                <li>Multilateral development banks &amp; impact funds</li>
                <li>Government institutions &amp; diplomatic missions</li>
                <li>Civil society &amp; NGO networks</li>
              </ul>
            </div>
            <div>
              <h3>Strategic deliverables:</h3>
              <ul>
                <li>Partner identification &amp; matching</li>
                <li>Due diligence coordination with local professionals</li>
                <li>Introduction to vetted legal and regulatory advisors</li>
                <li>Stakeholder relationship facilitation</li>
                <li>On-ground operational coordination</li>
                <li>Market intelligence and regulatory briefing</li>
                <li>Liaison and communication support</li>
                <li>Market entry strategy &amp; implementation planning</li>
              </ul>
            </div>
          </div>
        </section>
      </article>

      <article class="rasha-section">
        <p class="rasha-section__kicker">03 — Experience</p>
        <h1>
          Field Expertise, Market Development, Stakeholder Engagement, and
          Strategic Market Knowledge.
        </h1>
        <ul class="rasha-list">
          <li>
            Comprehensive market analysis and partner identification for
            Austrian Companies and international organizations seeking Syria
            operations
          </li>
          <li>
            Expert advisory on Syria market entry, regulatory landscape, and
            stakeholder relationship facilitation
          </li>
          <li>
            Regular field visits to Syria and the region; direct engagement with
            government officials, business leaders, legal professionals, and
            civil society stakeholders
          </li>
          <li>
            Research and briefings on Syria's business environment, investment
            opportunities, and regulatory landscape
          </li>
          <li>
            Facilitation of high-level introductions and dialogue between
            international stakeholders and Syrian partners, including legal and
            business professionals
          </li>
        </ul>
      </article>

      <article class="rasha-section">
        <p class="rasha-section__kicker">04 — Media &amp; Publications</p>
        <h1>Recognized Expert Voice on Syria, Regional Affairs, and Integration</h1>
        <ul class="rasha-list">
          <li>
            Press conferences with multiple Austrian government ministries
            (Interior, Defence, Europe) on integration and migration policy
          </li>
          <li>
            Media expert and commentator on Syria, regional affairs, and
            integration policy
          </li>
          <li>
            Published articles, media contributions, and broadcast appearances
            on Syria's political and economic developments, refugee integration,
            and European integration policy.
          </li>
        </ul>

        <div class="rasha-media">
          <section>
            <h2>Television &amp; Broadcast Appearances</h2>
            <p>
              Regular contributor to Austrian broadcast and print media on
              Syria, regional affairs, and integration policy
            </p>
            <h3>Television appearances:</h3>
            <ul class="rasha-links">
              <li v-for="link in televisionLinks" :key="link.href">
                <span>{{ link.label }}</span>
                <a :href="link.href" target="_blank" rel="noopener noreferrer">
                  {{ link.href }}
                </a>
              </li>
            </ul>
          </section>

          <section>
            <h2>Published Articles &amp; Commentary</h2>
            <p>
              on Syria's political and economic development, refugee
              integration, and European policy.
            </p>
            <ul class="rasha-links">
              <li v-for="link in articleLinks" :key="link.href">
                <span>{{ link.label }}</span>
                <a :href="link.href" target="_blank" rel="noopener noreferrer">
                  {{ link.href }}
                </a>
              </li>
            </ul>
          </section>

          <section>
            <h2>Interviews &amp; Featured Commentary</h2>
            <ul class="rasha-links">
              <li v-for="link in interviewLinks" :key="link.href">
                <span>{{ link.label }}</span>
                <a :href="link.href" target="_blank" rel="noopener noreferrer">
                  {{ link.href }}
                </a>
              </li>
            </ul>
            <p>
              Press Conference with the Austrian Minister of Interior. Die
              Presse: Innenministerium lanciert Gegen Marketing zu Schleppern
            </p>
            <ul class="rasha-links">
              <li>
                <span>Die Presse</span>
                <a
                  href="https://www.diepresse.com/6180761/innenministerium-lanciert-gegenmarketing-zu-schleppern"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://www.diepresse.com/6180761/innenministerium-lanciert-gegenmarketing-zu-schleppern
                </a>
              </li>
              <li>
                <span>Keynote speaker at international conferences and policy forums</span>
                <a
                  href="https://www.bbu.gv.at/oberoesterreichischer-integrationspreis-2024"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://www.bbu.gv.at/oberoesterreichischer-integrationspreis-2024
                </a>
              </li>
            </ul>
          </section>
        </div>
      </article>

      <article class="rasha-section">
        <p class="rasha-section__kicker">05 — Education &amp; Languages</p>
        <h1>Academic Excellence Complemented by Diplomatic Training</h1>
        <div class="rasha-timeline">
          <section v-for="item in education" :key="item.title">
            <p>{{ item.year }}</p>
            <h2>{{ item.title }}</h2>
            <span>{{ item.place }}</span>
          </section>
        </div>
        <h2 class="rasha-subtitle">Languages</h2>
        <ul class="rasha-list rasha-list--columns">
          <li>Arabic — Mother Tongue</li>
          <li>German — Fluent</li>
          <li>English — Fluent</li>
          <li>French — Good</li>
        </ul>
      </article>

      <article class="rasha-section">
        <p class="rasha-section__kicker">06 — Advisory &amp; Network</p>
        <h1>Trusted Advisor to Leading Institutions &amp; Market Players</h1>
        <p class="rasha-section__intro">
          A track record of facilitating successful partnerships between
          international stakeholders and Syrian partners, with strong
          relationships across business, legal, and professional communities.
        </p>
        <div class="rasha-card__columns">
          <section>
            <h2>Advisory Bodies &amp; Memberships</h2>
            <ul class="rasha-list">
              <li>
                Expert Advisory Board for Integration — Austrian Federal
                Chancellery
              </li>
              <li>Expert Advisory Board for Integration — City of Vienna</li>
              <li>Austrian Integration Fund (ÖIF) — Project Management</li>
              <li>Geneva Centre for Security Policy (GCSP) — former Fellow</li>
            </ul>
          </section>
          <section>
            <h2>Key Relationships &amp; Network</h2>
            <ul class="rasha-list">
              <li>
                Direct access to Syrian government officials, business leaders,
                and civil society figures
              </li>
              <li>
                Strong relationships with vetted legal professionals and
                regulatory experts in Syria
              </li>
              <li>Connections with Austrian &amp; international diplomatic missions</li>
              <li>
                Partnerships with development finance institutions, investors,
                and implementing organizations
              </li>
              <li>
                Participation in international dialogue initiatives on Syria's
                reconstruction and economic recovery
              </li>
            </ul>
          </section>
        </div>
      </article>

      <article class="rasha-section">
        <p class="rasha-section__kicker">Key Differentiators For Your Positioning</p>
        <div class="rasha-differentiators">
          <p v-for="item in differentiators" :key="item">
            <span>✓</span>{{ item }}
          </p>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
export default {
  name: "RashaProfessionalExperience",
  data() {
    return {
      expertise: [
        {
          title: "Partner Identification & Matching",
          text: "Identifying the right partner is critical to Syria market success. I leverage an extensive network across Syrian business, government, civil society, and professional communities to identify potential partners aligned with your organization's goals, values, and risk tolerance. Through field research and direct assessment, I evaluate partner credibility, capability, track record, and cultural fit before facilitating introductions.",
          for: "Investors, development agencies, international organizations, private-sector firms",
        },
        {
          title: "Due Diligence Coordination & Risk Assessment",
          text: "Before committing to a partnership or investment, comprehensive due diligence is essential. I coordinate on-ground assessments of potential partners—working with local legal professionals, business advisors, and regulatory experts to evaluate legal standing, financial health, compliance records, reputational risk, and alignment with international standards. I synthesize findings into clear risk assessments to inform your decision-making.",
          for: "Investment firms, multinational corporations, development finance institutions, fund managers",
        },
        {
          title: "Legal & Regulatory Partner Facilitation",
          text: "Syria's regulatory environment is complex. I facilitate connections between your organization and vetted local legal professionals and regulatory experts who can advise on legal structures, compliance requirements, contract negotiation, and ongoing regulatory navigation. I help coordinate communication and ensure your interests are understood and protected throughout the process.",
          for: "Corporations, international organizations, development agencies, legal teams",
        },
        {
          title: "On-Ground Operational Coordination",
          text: "Once partnerships are established, sustained advisory ensures smooth implementation. I provide ongoing coordination and relationship management between your organization and local partners, including legal professionals. I help navigate regulatory changes, facilitate problem-solving, and coordinate between your team and on-ground resources.",
          for: "Businesses with active operations, development program implementers, long-term investors",
        },
        {
          title: "Stakeholder Engagement & Relationship Facilitation",
          text: "Success in Syria depends on trust and understanding across diverse stakeholders. I facilitate introductions, manage dialogue, and build relationships between your organization and key decision-makers in government, business, and professional communities—ensuring alignment on objectives and mutual benefit.",
          for: "International organizations and business associations",
        },
      ],
      televisionLinks: [
        {
          label: "ORF [IM ZENTRUM - Integration: Wie schaffen wir das?]",
          href: "https://tv.orf.at/program/orf2/imzentrum396.html",
        },
        {
          label: "ORF (Stöckl Live: Miteinander reden- Integration oder Illusion?",
          href: "https://tv.orf.at/program/orf2/stoecklliv430.html",
        },
        {
          label: "Interview: Tyrol Tv",
          href: "https://www.dailymotion.com/video/x8i99du",
        },
      ],
      articleLinks: [
        {
          label: "Der Pragmaticus: Syrien-Wunden des Krieges.",
          href: "https://www.derpragmaticus.com/r/syrien-wunden-des-krieges",
        },
        {
          label: "Der Pragmaticus: Zurück nach Syrien",
          href: "https://www.derpragmaticus.com/r/zurueck-nach-syrien",
        },
        {
          label: "Die Presse: Sanktionen treiben Syrien in die armen des Iran",
          href: "https://www.diepresse.com/6205508/sanktionen-treiben-syrien-in-die-arme-des-iran",
        },
        {
          label: "Die Presse: Warum TikTok-Migranten wie Kanzler Nehammer ticken",
          href: "https://www.diepresse.com/18481497/warum-tiktok-migranten-wie-kanzler-nehammer-ticken",
        },
        {
          label: "Die Presse: Es gibt viele Gründe für ein striktes Kopftuchverbot",
          href: "https://www.diepresse.com/5597331/es-gibt-viele-gruende-fuer-ein-striktes-kopftuchverbot",
        },
        {
          label: "Die Presse: Rückkehr nach Raqqa",
          href: "https://www.diepresse.com/5369595/rueckkehr-nach-raqqa",
        },
        {
          label: "Frankfurter Allgemeine Zeitung: Wer heute noch feiert, wird morgen schon weinen",
          href: "https://www.faz.net/aktuell/feuilleton/debatten/lage-fuer-frauen-in-syrien-wer-heute-noch-feiert-wird-morgen-schon-weinen-110165541.html",
        },
      ],
      interviewLinks: [
        {
          label: "Frankfurter Allgemeine Zeitung",
          href: "https://www.faz.net/aktuell/feuilleton/debatten/interview-mit-der-syrischen-frauenrechtlerin-rasha-corti-15736213.html",
        },
        {
          label: "Frankfurter Allgemeine Zeitung",
          href: "https://www.faz.net/podcasts/f-a-z-podcast-fuer-deutschland/es-sind-dschihadisten-syrische-frauenrechtlerin-ueber-europaeische-naivitaet-110180582.html",
        },
        {
          label: "Kurier",
          href: "https://kurier.at/chronik/oesterreich/gebuertige-syrerin-gibt-videotipps-fuer-das-postkartenland/400657913",
        },
        {
          label: "Kurier",
          href: "https://kurier.at/politik/ausland/syrien-frauen-rechte-hts-assad-regime-herrschaft-islamisten/402990819",
        },
        {
          label: "Kurier",
          href: "https://kurier.at/chronik/oberoesterreich/christian-doerfel-landesrat-integration-wertekurse-deutschkurse/402977046",
        },
        {
          label: "Kurier",
          href: "https://kurier.at/chronik/oesterreich/drastische-sujets-online-kampagne-soll-migranten-abhalten/402120300",
        },
      ],
      education: [
        {
          year: "MSc (Master of Science): 2022 - 2025",
          title: "Executive Master of Science — International Migration Management",
          place: "Danube University Krems, Austria",
        },
        {
          year: "2023",
          title: "Diplomatic Tradecraft for non-diplomats",
          place: "Geneva Centre for Security Policy (GCSP), Switzerland",
        },
        {
          year: "2022 – 2023",
          title: "Public Speaking Programme",
          place: "Vienna, Austria",
        },
        {
          year: "2001 – 2005",
          title: "English Literature",
          place: "University of Damascus, Syria",
        },
      ],
      differentiators: [
        "Insider network — Direct relationships with Syrian government, business, legal, and civil society leaders",
        "Market expertise — Deep knowledge of business environment, regulatory landscape, stakeholder networks",
        "Partner facilitation — Ability to identify, assess, and introduce vetted partners",
        "Legal professional relationships — Strong connections with qualified local legal advisors (not legal services provider)",
        "Cultural fluency — Native Arabic speaker; understands business norms, decision-making, negotiation styles",
        "Field presence — Regular engagement in Syria; current, ground-truth market knowledge",
        "Neutral platform — Trusted by international organizations, government, and private sector",
        "Risk mitigation — Helps organizations avoid costly partnership mistakes through rigorous partner vetting and professional coordination",
        "Recognized expertise — Published author and media commentator on Syria, regional affairs, and integration policy",
      ],
    };
  },
};
</script>

<style scoped>
.rasha-profile {
  background: #ffffff;
  color: #20324f;
  padding: 72px 0;
}

.rasha-section {
  border-top: 1px solid rgba(19, 161, 199, 0.28);
  padding: 56px 0;
}

.rasha-section:first-child {
  border-top: 0;
  padding-top: 0;
}

.rasha-section__kicker {
  color: #13a1c7;
  font-family: "Poppins", sans-serif;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  margin: 0 0 18px;
  text-transform: uppercase;
}

.rasha-section h1 {
  color: #20324f;
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.08;
  margin: 0 0 32px;
  max-width: 1020px;
}

.rasha-section h2 {
  color: #20324f;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1.25;
  margin: 0 0 12px;
}

.rasha-section h3 {
  color: #13a1c7;
  font-family: "Poppins", sans-serif;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  margin: 18px 0 10px;
  text-transform: uppercase;
}

.rasha-section p,
.rasha-section li,
.rasha-section a,
.rasha-contact-grid p {
  font-size: 1.02rem;
  line-height: 1.7;
}

.rasha-section a {
  color: #13a1c7;
  overflow-wrap: anywhere;
}

.rasha-section__copy {
  display: grid;
  gap: 18px;
  max-width: 920px;
}

.rasha-section__copy p,
.rasha-section__intro {
  margin: 0;
}

.rasha-callout {
  background: #20324f;
  color: #ffffff;
  font-size: 1.02rem;
  line-height: 1.6;
  margin-top: 32px;
  max-width: 920px;
  padding: 24px;
}

.rasha-cards {
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 34px;
}

.rasha-card {
  border-left: 3px solid #13a1c7;
  background: #f7fbfd;
  padding: 26px;
}

.rasha-card p {
  margin: 0;
}

.rasha-card__for {
  color: #20324f;
  margin-top: 18px !important;
}

.rasha-card--wide {
  margin-top: 24px;
}

.rasha-card__columns {
  display: grid;
  gap: 36px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 24px;
}

.rasha-list {
  display: grid;
  gap: 14px;
  list-style: none;
  margin: 0;
  max-width: 980px;
  padding: 0;
}

.rasha-list li {
  padding-left: 24px;
  position: relative;
}

.rasha-list li::before {
  background: #13a1c7;
  content: "";
  height: 7px;
  left: 0;
  position: absolute;
  top: 0.68em;
  width: 7px;
}

.rasha-list--columns {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.rasha-media {
  display: grid;
  gap: 34px;
  margin-top: 34px;
}

.rasha-links {
  display: grid;
  gap: 12px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.rasha-links li {
  display: grid;
  gap: 4px;
}

.rasha-links span {
  font-weight: 800;
}

.rasha-timeline {
  display: grid;
  gap: 22px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.rasha-timeline section {
  border-left: 3px solid #13a1c7;
  padding-left: 22px;
}

.rasha-timeline p {
  color: #13a1c7;
  font-family: "Poppins", sans-serif;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  margin: 0 0 8px;
  text-transform: uppercase;
}

.rasha-timeline span {
  display: block;
  line-height: 1.5;
}

.rasha-subtitle {
  margin-top: 34px !important;
}

.rasha-contact-grid {
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.rasha-contact-grid p {
  margin: 0;
}

.rasha-differentiators {
  display: grid;
  gap: 14px;
  max-width: 980px;
}

.rasha-differentiators p {
  margin: 0;
  padding-left: 32px;
  position: relative;
}

.rasha-differentiators span {
  color: #13a1c7;
  font-weight: 800;
  left: 0;
  position: absolute;
}

@media (max-width: 1023px) {
  .rasha-cards,
  .rasha-card__columns,
  .rasha-timeline {
    grid-template-columns: 1fr;
  }

}

@media (max-width: 767px) {
  .rasha-profile {
    padding: 48px 0;
  }

  .rasha-section {
    padding: 42px 0;
  }

  .rasha-section h1 {
    font-size: 2.15rem;
  }

  .rasha-list--columns {
    grid-template-columns: 1fr;
  }
}
</style>
